import {
  Box, Button, Card, CardContent, CardHeader, Container, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Divider, Typography,
} from '@mui/material';
import { Field, Form, Formik } from 'formik';
import * as yup from 'yup';
import { TextField, Switch } from 'formik-mui';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import Spinner from '../components/Spinner';
import SupportedLanguages from '../models/enums/SupportedLanguages';
import { useAppDispatch, useAppSelector } from '../store/Hooks';
import translator from '../theme/translator.json';
import Utils from '../utils/Utils';
import User from '../models/User';
import UserService from '../services/UserService';
import AlertUtils from '../utils/AlertUtil';
import { auth } from '../FirebaseConfig';
import PromptForCredentials from '../components/PromptForCredentials';
import VisibilityState from '../models/enums/VisibilityState';
import LanguageSelector from '../components/languageSelector/LanguageSelector';

const Settings: React.FC = () => {
  const user = useAppSelector((state) => state.user.user);
  const language = useAppSelector((state) => state.user.language) as SupportedLanguages;
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [showPromptCredential, setShowPromptCredential] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const handlePromptCredentialAlert = (show: boolean) => {
    if (show) setShowPromptCredential(true);
    else setShowPromptCredential(false);
  };

  const updateUser = async (values: any) => {
    try {
      setLoading(true);
      const newName = values.name || user.name;
      const newPhone = values.phone || user.phone;
      let newShowAllInBanqueContacts = false;
      if (typeof values.showAllInBanqueContacts === 'boolean') {
        newShowAllInBanqueContacts = values.showAllInBanqueContacts;
      } else if (typeof user.showAllInBanqueContacts === 'boolean') {
        newShowAllInBanqueContacts = user.showAllInBanqueContacts;
      }

      let newHidePriority = false;
      if (typeof values.hidePriority === 'boolean') {
        newHidePriority = values.hidePriority;
      } else if (typeof user.hidePriority === 'boolean') {
        newHidePriority = user.hidePriority;
      }

      let newShowAdressField = false;
      if (typeof values.showAdressField === 'boolean') {
        newShowAdressField = values.showAdressField;
      } else if (typeof user.showAdressField === 'boolean') {
        newShowAdressField = user.showAdressField;
      }

      let newShowVendeurSuivis = false;
      if (typeof values.showVendeurSuivis === 'boolean') {
        newShowVendeurSuivis = values.showVendeurSuivis;
      } else if (typeof user.showVendeurSuivis === 'boolean') {
        newShowVendeurSuivis = user.showVendeurSuivis;
      }

      let newShowVisites = false;
      if (typeof values.showVisites === 'boolean') {
        newShowVisites = values.showVisites;
      } else if (typeof user.showVisites === 'boolean') {
        newShowVisites = user.showVisites;
      }

      // PDF Export settings
      let newPdfExportContactInformation = true;
      if (typeof values.pdfExportContactInformation === 'boolean') {
        newPdfExportContactInformation = values.pdfExportContactInformation;
      } else if (typeof user.pdfExportContactInformation === 'boolean') {
        newPdfExportContactInformation = user.pdfExportContactInformation;
      }

      let newPdfExportAdditionalInformation = true;
      if (typeof values.pdfExportAdditionalInformation === 'boolean') {
        newPdfExportAdditionalInformation = values.pdfExportAdditionalInformation;
      } else if (typeof user.pdfExportAdditionalInformation === 'boolean') {
        newPdfExportAdditionalInformation = user.pdfExportAdditionalInformation;
      }

      let newPdfExportClientDescription = true;
      if (typeof values.pdfExportClientDescription === 'boolean') {
        newPdfExportClientDescription = values.pdfExportClientDescription;
      } else if (typeof user.pdfExportClientDescription === 'boolean') {
        newPdfExportClientDescription = user.pdfExportClientDescription;
      }

      let newPdfExportFollowUpHistory = true;
      if (typeof values.pdfExportFollowUpHistory === 'boolean') {
        newPdfExportFollowUpHistory = values.pdfExportFollowUpHistory;
      } else if (typeof user.pdfExportFollowUpHistory === 'boolean') {
        newPdfExportFollowUpHistory = user.pdfExportFollowUpHistory;
      }

      const newUser = {
        ...user,
      } as User;
      newUser.name = newName;
      newUser.phone = newPhone;
      newUser.showAllInBanqueContacts = newShowAllInBanqueContacts;
      newUser.hidePriority = newHidePriority;
      newUser.showAdressField = newShowAdressField;
      newUser.showVendeurSuivis = newShowVendeurSuivis;
      newUser.showVisites = newShowVisites;
      newUser.pdfExportContactInformation = newPdfExportContactInformation;
      newUser.pdfExportAdditionalInformation = newPdfExportAdditionalInformation;
      newUser.pdfExportClientDescription = newPdfExportClientDescription;
      newUser.pdfExportFollowUpHistory = newPdfExportFollowUpHistory;

      await UserService.update(user.id, newUser);

      setLoading(false);
      await AlertUtils.createSuccessAlert(Utils.getTranslation(language, translator.successMessages.updateCompleted), dispatch);
    } catch (e: any) {
      setLoading(false);
      console.error(e);
      AlertUtils.createErrorAlert(Utils.getTranslation(language, translator.errorMessages.general.unknown), dispatch);
    }
  };

  const changeEmail = async (values: any) => {
    try {
      setLoading(true);
      const { newEmail } = values;
      const newUser = {
        ...user,
      } as User;
      newUser.email = newEmail;

      if (auth.currentUser) {
        await UserService.updateEmail(auth.currentUser, newEmail);
        await UserService.update(user.id, newUser);
      }

      setLoading(false);
      await AlertUtils.createSuccessAlert(Utils.getTranslation(language, translator.successMessages.updateCompleted), dispatch);
    } catch (e: any) {
      // Change email needs reauthantification if credentials have timed out
      if (e.code === 'auth/requires-recent-login') {
        setLoading(false);
        setShowPromptCredential(true);
      } else {
        setLoading(false);
        console.error(e);
        AlertUtils.createErrorAlert(Utils.getTranslation(language, translator.errorMessages.general.unknown), dispatch);
      }
    }
  };

  const changePassword = async (values: any) => {
    try {
      setLoading(true);
      const { password } = values;
      if (auth.currentUser) {
        await UserService.updatePassword(auth.currentUser, password);
      }

      setLoading(false);
      await AlertUtils.createSuccessAlert(Utils.getTranslation(language, translator.successMessages.updateCompleted), dispatch);
    } catch (e: any) {
      // Change password needs reauthantification if credentials have timed out
      if (e.code === 'auth/requires-recent-login') {
        setLoading(false);
        setShowPromptCredential(true);
      } else {
        setLoading(false);
        console.error(e);
        AlertUtils.createErrorAlert(Utils.getTranslation(language, translator.errorMessages.general.unknown), dispatch);
      }
    }
  };

  const deleteAccount = async () => {
    try {
      setLoading(true);
      if (auth.currentUser) {
        await UserService.update(auth.currentUser.uid, { visibility: VisibilityState.HIDDEN });
        UserService.deleteAccount(auth.currentUser, auth.currentUser.uid, user.stripe_sub_id);
      } else {
        throw Error('Delete account - Firebase user undefined');
      }
      setShowDeleteDialog(false);
      navigate('/signup');
      setLoading(false);
      await AlertUtils.createSuccessAlert(Utils.getTranslation(language, translator.successMessages.updateCompleted), dispatch);
    } catch (e: any) {
      // Delete account needs reauthantification if credentials have timed out
      if (e.code === 'auth/requires-recent-login') {
        setLoading(false);
        setShowPromptCredential(true);
      } else {
        setLoading(false);
        console.error(e);
        AlertUtils.createErrorAlert(Utils.getTranslation(language, translator.errorMessages.general.unknown), dispatch);
      }
    }
  };

  return (
    <>
      {user && <Layout title={Utils.getTranslation(language, translator.pages.settings.title)}>
        <Container
          maxWidth="lg"
          sx={{
            mt: 5,
          }}
        >
          <Card>
            <CardHeader
              title={Utils.getTranslation(language, translator.pages.settings.changeLanguage)}
            />
            <Divider />
            <CardContent>
              <LanguageSelector />
            </CardContent>
          </Card>
        </Container>
        <Container
          maxWidth="lg"
          sx={{
            mt: 3,
          }}
        >
          {/**
           *  Paramètres CRM
           */}
          <Formik
            initialValues={{
              showAllInBanqueContacts: user?.showAllInBanqueContacts || false,
              hidePriority: user?.hidePriority || false,
              showAdressField: user?.showAdressField || false,
              showVendeurSuivis: user?.showVendeurSuivis || false,
              showVisites: user?.showVisites || false,
              pdfExportContactInformation: user?.pdfExportContactInformation ?? true,
              pdfExportAdditionalInformation: user?.pdfExportAdditionalInformation ?? true,
              pdfExportClientDescription: user?.pdfExportClientDescription ?? true,
              pdfExportFollowUpHistory: user?.pdfExportFollowUpHistory ?? true,
            }}
            validationSchema={yup.object({
              showAllInBanqueContacts: yup.bool(),
              hidePriority: yup.bool(),
              showAdressField: yup.bool(),
              showVendeurSuivis: yup.bool(),
              showVisites: yup.bool(),
              pdfExportContactInformation: yup.bool(),
              pdfExportAdditionalInformation: yup.bool(),
              pdfExportClientDescription: yup.bool(),
              pdfExportFollowUpHistory: yup.bool(),
            })}
            onSubmit={(values, { setSubmitting }) => {
              updateUser(values);
              setSubmitting(false);
            }}
          >
            {(formikProps) => (
              <Form>
                <Card>
                  <CardHeader
                    title="Options du CRM"
                    subheader="Modifiez l'affichage et le comportement du CRM"
                  />
                  <Divider />
                  <CardContent>
                    <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                      <p>Afficher tous les contacts dans la banque de contacts?</p>
                      <Field
                        component={Switch}
                        name="showAllInBanqueContacts"
                        type="checkbox"
                        margin='normal'
                      />
                    </Box>
                    <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                      <p>Masquer la priorité?</p>
                      <Field
                        component={Switch}
                        name="hidePriority"
                        type="checkbox"
                        margin='normal'
                      />
                    </Box>
                    <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                      <p>Afficher le champ adresse?</p>
                      <Field
                        component={Switch}
                        name="showAdressField"
                        type="checkbox"
                        margin='normal'
                      />
                    </Box>
                    <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                      <p>Afficher la colonne vendeur dans les suivis?</p>
                      <Field
                        component={Switch}
                        name="showVendeurSuivis"
                        type="checkbox"
                        margin='normal'
                      />
                    </Box>
                    <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                      <p>Afficher les visites?</p>
                      <Field
                        component={Switch}
                        name="showVisites"
                        type="checkbox"
                        margin='normal'
                      />
                    </Box>
                    <Typography variant="h6" sx={{ mt: 3, mb: 2, fontWeight: 'bold' }}>
                      {Utils.getTranslation(language, translator.pages.settings.pdfExport.title)}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                      {Utils.getTranslation(language, translator.pages.settings.pdfExport.subheader)}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                      <p>{Utils.getTranslation(language, translator.pages.settings.pdfExport.contactInformation)}</p>
                      <Field
                        component={Switch}
                        name="pdfExportContactInformation"
                        type="checkbox"
                        margin='normal'
                      />
                    </Box>
                    <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                      <p>{Utils.getTranslation(language, translator.pages.settings.pdfExport.additionalInformation)}</p>
                      <Field
                        component={Switch}
                        name="pdfExportAdditionalInformation"
                        type="checkbox"
                        margin='normal'
                      />
                    </Box>
                    <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                      <p>{Utils.getTranslation(language, translator.pages.settings.pdfExport.clientDescription)}</p>
                      <Field
                        component={Switch}
                        name="pdfExportClientDescription"
                        type="checkbox"
                        margin='normal'
                      />
                    </Box>
                    <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                      <p>{Utils.getTranslation(language, translator.pages.settings.pdfExport.followUpHistory)}</p>
                      <Field
                        component={Switch}
                        name="pdfExportFollowUpHistory"
                        type="checkbox"
                        margin='normal'
                      />
                    </Box>
                  </CardContent>
                  <Divider />
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      p: 2,
                    }}
                  >
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      sx={{ mt: 3, mb: 2 }}
                      disabled={formikProps.isSubmitting}
                    >
                      {Utils.getTranslation(language, translator.pages.settings.information.submit)}
                    </Button>
                  </Box>
                </Card>
              </Form>
            )}
          </Formik>
        </Container>
        <Container
          maxWidth="lg"
          sx={{
            mt: 3,
          }}
        >
          {/**
           *  Personal information form
           */}
          <Formik
            initialValues={{
              name: user?.name,
              phone: user?.phone,
            }}
            validationSchema={yup.object({
              name: yup.string().required(Utils.getTranslation(language, translator.formMessages.requiredField)),
              phone: yup.string().required(Utils.getTranslation(language, translator.formMessages.requiredField)),
            })}
            onSubmit={(values, { setSubmitting }) => {
              updateUser(values);
              setSubmitting(false);
            }}
          >
            {(formikProps) => (
              <Form>
                <Card>
                  <CardHeader
                    title={Utils.getTranslation(language, translator.pages.settings.information.title)}
                    subheader={Utils.getTranslation(language, translator.pages.settings.information.subheader)}
                  />
                  <Divider />
                  <CardContent>
                    <Field
                      component={TextField}
                      name="name"
                      type="text"
                      label={Utils.getTranslation(language, translator.pages.settings.information.inputs.nameLabel)}
                      margin='normal'
                      fullWidth
                    />
                    <Field
                      component={TextField}
                      name="phone"
                      type="text"
                      label={Utils.getTranslation(language, translator.pages.signup.phone)}
                      margin='normal'
                      fullWidth
                    />
                  </CardContent>
                  <Divider />
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      p: 2,
                    }}
                  >
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      sx={{ mt: 3, mb: 2 }}
                      disabled={formikProps.isSubmitting}
                    >
                      {Utils.getTranslation(language, translator.pages.settings.information.submit)}
                    </Button>
                  </Box>
                </Card>
              </Form>
            )}
          </Formik>
        </Container>

        {/**
           *  Personal information form
           */}
        <Container
          maxWidth="lg"
          sx={{
            marginTop: '20px',
          }}
        >
          <Formik
            initialValues={{
              newEmail: '',
            }}
            validationSchema={yup.object({
              newEmail: yup.string().required(Utils.getTranslation(language, translator.formMessages.requiredField)),
            })}
            onSubmit={(values, { setSubmitting, resetForm }) => {
              changeEmail(values);
              setSubmitting(false);
              resetForm();
            }}
          >
            {(formikProps) => (
              <Form>
                <Card>
                  <CardHeader
                    title={Utils.getTranslation(language, translator.pages.settings.changeEmail.title)}
                    subheader={user?.email}
                  />
                  <Divider />
                  <CardContent>
                    <Field
                      component={TextField}
                      name="newEmail"
                      type="text"
                      label={Utils.getTranslation(language, translator.pages.settings.changeEmail.inputs.email)}
                      margin='normal'
                      fullWidth
                    />
                  </CardContent>
                  <Divider />
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      p: 2,
                    }}
                  >
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      sx={{ mt: 3, mb: 2 }}
                      disabled={formikProps.isSubmitting}
                    >
                      {Utils.getTranslation(language, translator.pages.settings.changeEmail.submit)}
                    </Button>
                  </Box>
                </Card>
              </Form>
            )}
          </Formik>
        </Container>

        {/**
           *  Password form
           */}
        <Container
          maxWidth="lg"
          sx={{
            marginTop: '20px',
          }}
        >
          <Formik
            initialValues={{
              password: '',
              passwordConfirmation: '',
            }}
            validationSchema={yup.object({
              password: yup.string().min(6).required(Utils.getTranslation(language, translator.formMessages.requiredField)),
              passwordConfirmation: yup.string()
                .required(Utils.getTranslation(language, translator.formMessages.requiredField))
                .oneOf([yup.ref('password'), null], Utils.getTranslation(language, translator.formMessages.passwordsMustMatch)),
            })}
            onSubmit={(values, { setSubmitting, resetForm }) => {
              changePassword(values);
              setSubmitting(false);
              resetForm();
            }}
          >
            {(formikProps) => (
              <Form>
                <Card>
                  <CardHeader
                    title={Utils.getTranslation(language, translator.pages.settings.changePassword.title)}
                  />
                  <Divider />
                  <CardContent>
                    <Field
                      component={TextField}
                      name="password"
                      type="password"
                      label={Utils.getTranslation(language, translator.pages.settings.changePassword.inputs.password)}
                      margin='normal'
                      fullWidth
                    />
                    <Field
                      component={TextField}
                      name="passwordConfirmation"
                      type="password"
                      label={Utils.getTranslation(language, translator.pages.settings.changePassword.inputs.passwordConfirmation)}
                      margin='normal'
                      fullWidth
                    />
                  </CardContent>
                  <Divider />
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      p: 2,
                    }}
                  >
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      sx={{ mt: 3, mb: 2 }}
                    >
                      {Utils.getTranslation(language, translator.pages.settings.changePassword.submit)}
                    </Button>
                  </Box>
                </Card>
              </Form>
            )}
          </Formik>
        </Container>
        {/**
           *  Deleting account form
           */}
        <Container
          maxWidth="lg"
          sx={{
            marginTop: '20px',
            marginBottom: '20px',
          }}
        >
          <Card sx={{
            backgroundColor: '#D09797',
            border: '2px solid red',
          }}>
            <CardHeader
              title={
                <Typography variant='h5' sx={{ color: 'white' }}>
                  {Utils.getTranslation(language, translator.pages.settings.deleteAccount.title)}
                </Typography>
              }
              subheader={
                <Typography sx={{ color: 'white' }}>
                  {Utils.getTranslation(language, translator.pages.settings.deleteAccount.subheader)}
                </Typography>
              }
            />
            <CardContent>
              <Button
                variant='contained'
                color="error"
                onClick={() => setShowDeleteDialog(true)}
              >
                {Utils.getTranslation(language, translator.pages.settings.deleteAccount.submit)}
              </Button>
            </CardContent>
          </Card>
        </Container>
      </Layout>}
      <Spinner show={loading}/>
      <PromptForCredentials show={showPromptCredential} setShow={handlePromptCredentialAlert} />
      <Dialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {Utils.getTranslation(language, translator.pages.settings.deleteAccount.promptTitle)}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {Utils.getTranslation(language, translator.pages.settings.deleteAccount.promptDescription)}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)}>{Utils.getTranslation(language, translator.general.cancel)}</Button>
          <Button onClick={() => deleteAccount()} autoFocus>
            {Utils.getTranslation(language, translator.pages.settings.deleteAccount.submit)}
          </Button>
        </DialogActions>
      </Dialog>
    </>

  );
};

export default Settings;
